/**
 * Centralized error handling utilities for the frontend.
 *
 * Provides consistent error message extraction, retry detection,
 * and toast duration constants.
 */

/**
 * Extract a human-readable error message from any error type.
 *
 * Handles:
 * - Error instances
 * - String errors
 * - API response objects with `detail` field
 * - Unknown error types (returns fallback)
 */
export function extractErrorMessage(
  error: unknown,
  fallback = "An error occurred",
): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  // Handle FastAPI error response format: { detail: "message" }
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string") {
      return detail;
    }
    // Handle array of validation errors
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === "object" && first && "msg" in first) {
        return String(first.msg);
      }
    }
  }

  // Handle generic message field
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }

  return fallback;
}

/**
 * Check if an HTTP status code indicates a retryable error.
 *
 * Retryable errors are typically transient and may succeed on retry:
 * - 408: Request Timeout
 * - 429: Too Many Requests
 * - 500: Internal Server Error
 * - 502: Bad Gateway
 * - 503: Service Unavailable
 * - 504: Gateway Timeout
 */
export function isRetryableError(status: number): boolean {
  return [408, 429, 500, 502, 503, 504].includes(status);
}

/**
 * Standard toast notification durations in milliseconds.
 */
export const TOAST_DURATIONS = {
  error: 4000,
  success: 2000,
  info: 3000,
  warning: 3500,
} as const;

/**
 * Common error messages for reuse.
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  SERVER_ERROR: "Server error. Please try again later.",
  UNAUTHORIZED: "Session expired. Please log in again.",
  NOT_FOUND: "The requested resource was not found.",
  VALIDATION_FAILED: "Please check your input and try again.",
  RATE_LIMITED: "Too many requests. Please slow down.",
} as const;
